<script setup lang="ts">
import { ref } from 'vue'
import { NButton, NInput } from 'naive-ui'

// The Ask drawer's composer: one bordered field holding the textarea, a
// hint line and the action button. While an answer is in flight the
// mint Ask button becomes Stop, which aborts the request via the
// parent's `threads.cancel()`.
const content = defineModel<string>({ required: true })

defineProps<{
  followUp: boolean
  submitting: boolean
  canSubmit: boolean
  submitError: string | null
}>()

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'stop'): void
}>()

// The textarea sits borderless inside the composer's own outlined box.
const INPUT_THEME = { color: 'transparent', colorFocus: 'transparent', fontSizeMedium: '15px', paddingMedium: '0' }

const input = ref<InstanceType<typeof NInput> | null>(null)

// Cmd+Enter / Ctrl+Enter submits — chat-app muscle memory. Plain Enter
// still inserts a newline because the textarea autosizes for
// multi-paragraph follow-ups.
function onEnter(event: KeyboardEvent): void {
  if (event.key !== 'Enter') return
  if (!event.metaKey && !event.ctrlKey) return
  event.preventDefault()
  emit('submit')
}

function focus(): void {
  input.value?.focus()
}

defineExpose({ focus })
</script>

<template>
  <form class="flex w-full flex-col gap-2" @submit.prevent="emit('submit')">
    <div
      class="flex flex-col gap-2 rounded-2xl border border-border-control bg-surface-bg pb-2.5 pl-4 pr-3 pt-3 focus-within:border-brand-primary"
    >
      <label for="thread-composer-input" class="sr-only">{{ followUp ? 'Follow-up question' : 'Question' }}</label>
      <NInput
        ref="input"
        v-model:value="content"
        type="textarea"
        :bordered="false"
        :theme-overrides="INPUT_THEME"
        :autosize="{ minRows: 2, maxRows: 8 }"
        :placeholder="followUp ? 'Ask a follow-up…' : 'Ask the brain a question…'"
        :input-props="{ id: 'thread-composer-input', spellcheck: true }"
        data-testid="thread-content"
        @keydown="onEnter"
      />
      <div class="flex items-center gap-2">
        <span class="flex-1 text-xs text-ink-faint">Answers cite wiki pages · ⌘/Ctrl ↵ to send</span>
        <NButton
          v-if="submitting"
          attr-type="button"
          class="min-h-10 px-4 font-semibold"
          data-testid="thread-cancel"
          @click="emit('stop')"
        >
          <span aria-hidden="true" class="mr-1.5 inline-block h-2.5 w-2.5 rounded-[2px] bg-current" />
          Stop
        </NButton>
        <NButton
          v-else
          type="primary"
          attr-type="submit"
          class="min-h-10 px-[18px] font-semibold"
          :disabled="!canSubmit"
          data-testid="thread-submit"
        >
          Ask
        </NButton>
      </div>
    </div>
    <p v-if="submitError" class="m-0 text-sm text-status-alert" role="alert">{{ submitError }}</p>
  </form>
</template>
